var _      = require('lodash');
var Banzai = require('./banzai');

var banzaiDefinitions = {

  // for each feature:
  // - name,
  // - description
  // - intendedExpiry: string of date to remove,
  // - type: ['boolean', 'string', 'number']
  //         These must be the same as what `typeof` outputs. They are
  //         used in the DSL interpretation.
  // - "default": an expression in our DSL to use if no override is
  //              provided via dynamodb/bless. default is a js keyword so quote it.
  // - errorFallback: what to fall back to if the DSL value cannot be interpreted.

  // NOTE when deleting feature flags be sure not to remove flags used in testing. See file
  // builder/test/support/test_banzai-features.js

  //////////////////////////////////////////////////
  // dev related features

  debugMode: {
    type: 'boolean',
    errorFallback: false,
    "default": {"envIs": "development"},
    description: 'Extra error checking and logging + debug features'
  },

  safeMode: {
    type: 'boolean',
    errorFallback: false,
    "default": false,
    description: (
      'Enter a debugger immediately after the pageData is loaded in the editor.' +
      ' This is useful for fixing bad poms. Only active if debugMode=1 and safeMode=1')
  },

  debugBgPropUpgrades: {
    type: 'boolean',
    errorFallback: false,
    "default": false,
    description: 'Log the upgrading of old->new background props'
  },

  showRxUIEvents: {
    type: 'boolean',
    errorFallback: false,
    "default": false,
    description: 'Console.log rxUi events.'
  },

  showJuiEvents: {
    type: 'boolean',
    errorFallback: false,
    "default": false,
    description: 'Console.log jui/lp events. The guts.'
  },

  maxRxUIEventsToBuffer: {
    type: 'number',
    errorFallback: 30,
    "default": 30,
    description: 'Max number of rxUi events to capture into the ring buffer'
  },

  recordRxUIEvents: {
    type: 'boolean',
    errorFallback: false,
    "default": {"isFeatureEnabled": "debugMode"},
    description: 'Capture/record rxUi events.'
  },

  replayRxRecording: {
    type: 'string',
    errorFallback: null,
    "default": null,
    description: 'rxUi recording to auto-replay after editor load. requires recordRxUIEvents'
  },

  rxRecordingReplaySpeed: {
    type: 'number',
    errorFallback: 2.0,
    "default": 2.0,
    description: 'Time scaling factor for replay of recordRxUIEvents'
  },

  checkInvariants: {
    type: 'boolean',
    errorFallback: false,
    "default": {"isFeatureEnabled": "debugMode"},
    description: 'Check global invariants throughout editing session'
  },

  checkInvariantsThrowOnAssertException: {
    type: 'boolean',
    errorFallback: false,
    "default": false,
    description: 'Raise an exception if an invariant fails'
  },

  contentTypeOverride : {
    type           : 'string',
    errorFallback  : null,
    'default'      : null,
    description    : 'Can be used to override the content type of the current page. ' +
      'Should only be used in dev, or by admins when creating templates for a new ' +
      'content type.',
    intendedExpiry : null,
  },

  //////////////////////////////////////////////////
  // user facing features:

  inputtableDropdown: {
    type: 'boolean',
    errorFallback: false,
    "default": true,
    description: 'dropdown with input on inline text panel',
    intendedExpiry: '2017-01-30'
  },

  fontPicker: {
    type: 'boolean',
    errorFallback: false,
    "default": {"envIs": "development"},
    description: 'show font picker button and modal',
    intendedExpiry: null
  },

  pomChangeEvents: {
    type: 'boolean' ,
    errorFallback: true,
    "default": true,
    description: 'POM change events',
    intendedExpiry: null,
    notes: 'We keep this flag to be able to disable in test mode. Off in tests.'
  },

  // Do not delete. This is being used for recording playback in builder-tools.
  republishing: {
    type: 'boolean',
    errorFallback: true,
    "default": true,
    description: 'Republish from the builder',
    intendedExpiry: 'Never'
  },

  // DO not delete. This is being used on some customer pages.
  renameWindowModuleToUbModule: {
    type: 'boolean',
    errorFallback: false,
    "default": false,
    description: 'Allows us to use ubModule instead of window.module on forms',
    intendedExpiry: 'Never'
  },

  externalLightboxes: {
    type: 'boolean',
    errorFallback: false,
    "default": {any: [{"envIs": "integration"}, {"envIs": "development"}]},
    description: 'Lightbox option for outgoing links',
    intendedExpiry: '2015-08-01',
    eventTag: 'External lightbox feature flag enabled'
  },

  useGoalLinkWrapperOnInternalLightboxes: {
    type: 'boolean',
    errorFallback: false,
    "default": false,
    description: 'Use conversion goal link wrappers on internal lightbox links',
    intendedExpiry: '2016-02-01',
  },

  betterFieldClean: {
    type: 'boolean',
    errorFallback: true,
    "default": true,
    description: "Mobile-aware field cleaner",
    intendedExpiry: '2015-11-01'
  },

  buttonsInheritStyles: {
    type: 'boolean',
    errorFallback: false,
    "default": {any: [{"envIs": "integration"}, {"envIs": "development"}]},
    description: 'New buttons inherit stylistic properties of the last button added to the page',
    intendedExpiry: '2015-01-01'
  },

  inheritTextStyle: {
    type: 'boolean',
    errorFallback: false,
    default: {any: [{envIs: 'integration'}, {envIs: 'development'}]},
    description: 'New text elements inherit the font and text style from the last-edited text element',
    intendedExpiry: '2018-07-01'
  },

  imageDragAndDropUpload: {
    type: 'boolean',
    errorFallback: false,
    "default": {any: [{"envIs": "integration"}, {"envIs": "development"}]},
    description: 'Allows users to drag and drop images from their desktop',
    intendedExpiry: '2016-03-01'
  },

  dataOnFormPost: {
    type: 'boolean',
    errorFallback: false,
    "default": true,
    description: 'Show the append data checkbox on post to URL option',
    intendedExpiry: '2015-05-01'
  },

  saveAsTemplate: {
    type: 'boolean',
    errorFallback: false,
    "default": {any: [{"envIs": "integration"}, {"envIs": "development"}]},
    description: 'Show the save as template option on toolbar'
  },

  betterPhoneValidation: {
    type: 'boolean',
    errorFallback: true,
    default: true,
    description: 'use more detailed regex for phone number validation on published pages'
  },

  responsiveConvertables: {
    type: 'boolean',
    errorFallback: false,
    default: false,
    description: 'Enable responsive controls for Convertables content types'
  },

  scrollContentTree: {
    type: 'boolean',
    errorFallback: true,
    default: true,
    description: 'use scroll animation to scroll to element in content tree'
  },

  newBreakpointToggle: {
    type: 'boolean',
    errorFallback: false,
    default: false,
    description: 'Use toggles inlined into breakpoint switch buttons',
    intendedExpiry: '2017-06-01'
  },

  rootDisplayPositionControls: {
    type: 'boolean',
    errorFallback: false,
    default: false,
    description: 'Show top/bottom position control, used for Sticky Bars',
    intendedExpiry: '2017-08-01'
  },

  formBuilderPlaceholderInput: {
    type: 'boolean',
    errorFallback: false,
    default: false,
    description: 'Shows an editable placeholder field in the form builder',
    intendedExpiry: '2017-10-01'
  },

  allowFormColumns: {
    type: 'boolean',
    errorFallback: false,
    default: false,
    description: 'Allows forms to be placed in columns',
    intendedExpiry: '2017-10-01'
  },

  absolutePositionFields: {
    type: 'boolean',
    errorFallback: false,
    default: {"envIs": "development"},
    description: 'Stores flexbox field geometry on save. Then renders absolute positioned fields on the published page',
    intendedExpiry: '2017-10-01'
  },

  formColumnsOnPagesAndOverlays: {
    type: 'boolean',
    errorFallback: false,
    default: false,
    description: 'Enabled allowFormColumns on pages and overlays',
    intendedExpiry: '2017-10-01'
  },

  alignElements: {
    type: 'boolean',
    errorFallback: false,
    default: false,
    description: 'Shows alignment menu on the canvas',
    intendedExpiry: '2018-08-01'
  },

  retinaImages: {
    type: 'boolean',
    errorFallback: false,
    default: false,
    description: 'Publish 2x and 3x versions of images when original version of image is large enough',
    intendedExpiry: '2018-07-01',
  },

  imageCompression: {
    type: 'boolean',
    errorFallback: false,
    default: {any: [{envIs: 'integration'}, {envIs: 'development'}]},
    description: 'Allow users to toggle the level of image compression.',
    intendedExpiry: '2018-09-01',
  },

  singleClickTextEditing: {
    type: 'boolean',
    errorFallback: false,
    default: {any: [{envIs: 'integration'}, {envIs: 'development'}]},
    description: 'Show text/font style controls when text element is selected but not in edit mode',
    intendedExpiry: '2018-07-01'
  },

  unsplash: {
    type: 'boolean',
    errorFallback: false,
    "default": {any: [{envIs: "development"}, {envIs: "test"}]},
    description: 'Unsplash source in the asset library',
    intendedExpiry: '2018-08-01'
  },

  //////////////////////////////////////////////////
  // experimental features:

  skipPanelSyncUIOnSetElement: {
    type: 'boolean',
    errorFallback: false,
    "default": {"any": [{envIs: 'development'}, {envIs: 'test'}]},
    description: 'Skip a potentially redundant call to syncUIControls'
  },

  sampledCanvasListenerOnScroll: {
    type: 'boolean',
    errorFallback: false,
    "default": {any: [{envIs: "development"}, {envIs: "test"}]},
    description: 'Sample call to a canvasListener on scroll'
  },

  skipPageUpdateAndRefreshOnReorder: {
    type: 'boolean',
    errorFallback: false,
    "default": {"any": [{envIs: 'development'}, {envIs: 'test'}]},
    description: 'Skip a potentially redundant call to page.updateAndRefresh when ' +
      'sections are reordered'
  },

  useEditorUndoStack: {
    type: 'boolean',
    errorFallback: false,
    "default": {any: [{envIs: "development"}, {envIs: "test"}]},
    description: 'Use a shared editor undo stack instead of having a undo stack per page'
  },

  newAssetLibrary: {
    type: 'boolean',
    errorFallback: false,
    "default": {any: [{envIs: "development"}, {envIs: "test"}]},
    description: 'New React-based asset library with improved performance'
  },

  resizeBackgroundImages: {
    type: 'boolean',
    errorFallback: false,
    "default": {any: [{"envIs": "integration"}, {"envIs": "development"}, {"envIs": "test"}]},
    description: 'Resize background images to match container, for smaller file sizes'
  },

  //////////////////////////////////////////////////
  // release management gates for other changes such as refactorings
  // or bug fixes which we're not 100% confident in:

  perfSampling: {
    type: 'boolean' ,
    errorFallback: false,
    "default": false,
    description: 'Log performance samples so that we are aware of long running actions'
  },

  removeColorOverlayBorders: {
    type: 'boolean' ,
    errorFallback: false,
    "default": true,
    description: 'Remove borders in color-overlays that are inherited from their parent page section'
  },

  activePerfSamples: {
    type: ['string'] ,
    errorFallback: [],
    "default": ['breakpointChanged', 'pageChanged'],
    description: 'Performance samples to turn on'
  },

  rmElementViewWrappers: {
    type: 'boolean' ,
    errorFallback: false,
    "default": {any: [{envIs: "development"}, {envIs: "test"}]},
    description: 'Deprecate visible_element_view_wrapper_methods.js. Will throw if used.'
  },

  skipExtraSwitchbreakpoint: {
    type: 'boolean',
    errorFallback: false,
    "default": {any: [{envIs: "development"}, {envIs: "test"}]},
    intendedExpiry: "2015-10-01",
    description: "Skip redundant call to switchToBreakpoint from setActivePage"
  },

  versionControlEventTracking: {
    type: 'boolean',
    errorFallback: false,
    "default": false,
    intendedExpiry: "2016-05-01",
    description: "Tracks the number of saves loaded within version control"
  },

  getVisibilityByModel: {
    type: 'boolean',
    errorFallback: true,
    "default": true,
    intendedExpiry: "2016-04-01",
    description: "Query element visibility by its model"
  },

  removeDetachedElementsDryRun: {
    type: 'boolean',
    errorFallback: false,
    "default": false,
    description: 'Logs the list of detached elements that should be removed to sentry'
  },

  removeDetachedElements: {
    type: 'boolean',
    errorFallback: false,
    "default": false,
    description: 'removes detached elements from the page. To see what this will do instead run with dry run.'
  },

  runFontStorageUpgrader: {
    type: 'boolean',
    errorFallback: true,
    "default": true,
    description: 'Runs upgrader 2.8 that migrates the existing font storage to the new storage'
  },

  trackPublishViaSNS: {
    type: 'boolean',
    errorFallback: false,
    "default": false,
    description: 'track publish event in events gateway'
  },

  //////////////////////////////////////////////////
  // configuration
  eventsGatewayHostname: {
    type: 'string',
    errorFallback: 'events-gateway.unbounce.com',
    "default": 'events-gateway-test.unbounce.com',
    // Override is set to events-gateway.unbounce.com on production
    description: 'Hostname of lp-events-gateway'
  },

  pollerHostname: {
    type: 'string',
    // If the value given here is an empty string, we default to using 'window.location.host'
    // Only if we set a override through bless will it take this feature flag value
    errorFallback: '',
    "default": '',
    // Override is set to poll.unbounce.com on production
    description: 'Hostname of builder poller host'
  },

  pomSavesGatewayURL: {
    type: 'string',
    errorFallback: 'https://dqm8o1z2e1.execute-api.us-east-1.amazonaws.com/production/pomsave',
    "default": 'https://dqm8o1z2e1.execute-api.us-east-1.amazonaws.com/production/pomsave',
    // Override is set to https://dqm8o1z2e1.execute-api.us-east-1.amazonaws.com/production/pomsave-integration on integration.
    // API Gateways CORS settings for the production endpoint only allows the domain app.unbounce.com. So any attempts to POST
    // from production/devleopment environments will be rejected
    description: 'POST URL for pom saves lambda'
  },

  perfSamplingPeriod: {
    type: 'number',
    errorFallback: 60 * 1000,
    "default": 60 * 1000,
    description: 'interval upon which to send performace data via sns'
  },

  perfSamplingMaxSize: {
    type: 'number',
    errorFallback: 1000,
    "default": 1000,
    description: 'The max size of the buffer for perf sampling'
  },

  backgroundImageMinSize: {
    type: 'number',
    errorFallback: 200,
    "default": 200,
    description: 'The min size of the background image to determine the UI defaults'
  },

  republishTimeoutPeriod: {
    type: 'number',
    errorFallback: 3000,
    "default": 3000,
    description: 'The default timeout for making a republish request from the editor'
  },

  republishErrorRetryAttempts: {
    type: 'number',
    errorFallback: 3,
    "default": 3,
    description: 'The amount of times we try to republish again after seeing an error'
  },

  publishStatePollerFast: {
    type: 'number',
    errorFallback: 500,
    "default": 500,
    description: 'The speed in which we poll the server to find the state of publishing'
  },

  publishStatePollerSlow: {
    type: 'number',
    errorFallback: 15000,
    "default": 15000,
    description: 'The speed in which we poll the server to find the state of publishing'
  },

  flatUITheme: {
    type: 'string',
    errorFallback: "nighthawks",
    "default": "nighthawks",
    description: "Choose the theme for flat UI"
  },

  lightboxLimit: {
    type: 'number',
    errorFallback: 1,
    "default": 1,
    description: 'The max number of lightboxes allowed in the builder'
  },

  fontPaginationLimit: {
    type: 'number',
    errorFallback: 21,
    "default": 21,
    description: 'The number of font nodes we lazy load at a time in the font picker'
  },

  fontTimeBuffer: {
    type: 'number',
    errorFallback: 125,
    "default": 125,
    description: 'The time buffer in milliseconds between batches of new fonts being loaded'
  },

  fontCountBuffer: {
    type: 'number',
    errorFallback: 40,
    "default": 40,
    description: 'The maximum number of fonts to load in any request'
  },

  fontSearchThreshold: {
    type: 'number',
    errorFallback: 30,
    "default": 30,
    description: 'The percentage strictness of font fuzzy searching in font picker. Lower is stricter'
  },

  newLoginModal: {
    type: 'boolean',
    errorFallback: false,
    default: {envIs: 'development'},
    description: 'Whether we should add the new login modal to the builder',
    intendedExpiry: '2017-09-01'
  },

  builderLoginEndpoint: {
    type: 'string',
    errorFallback: '/users/sign_in.json',
    "default": '/users/sign_in.json',
    description: 'The endpoint to hit for re-authentication in the builder'
  },

  builderWebappGoogleOauth2Callback: {
    type: 'string',
    errorFallback: '/users/auth/google_oauth2/callback.json',
    "default": '/users/auth/google_oauth2/callback.json',
    description: 'The endpoint to hit for re-authentication usign Google SSO in the builder'
  },

  googleSso: {
    type: 'boolean',
    errorFallback: false,
    default: false,
    description: 'Google Single Sign On'
  },

  googleSsoCredentialKey: {
    type: 'string',
    errorFallback: '214338196915-a5bb7j81re1r6eihphfp83437kecfm9u.apps.googleusercontent.com',
    default: '214338196915-a5bb7j81re1r6eihphfp83437kecfm9u.apps.googleusercontent.com',
    description: 'Google Single Sign On Credential clientId key'
  },

  maximumNumberOfFonts: {
    type: 'number',
    errorFallback: 25,
    "default": 25,
    description: 'Maximum number of fonts we allow users to add to their page'
  },

  unsplashAccessKey: {
    type: 'string',
    errorFallback: 'de2e8b566492d6222df5813c5246bd0c85e366cc8e2396e364c4827ca30e8bc4',
    default: 'de2e8b566492d6222df5813c5246bd0c85e366cc8e2396e364c4827ca30e8bc4',
    description: 'Unsplash API access key - https://unsplash.com/oauth/applications/27955'
  },

  sandbox: {
    type: 'boolean',
    errorFallback: false,
    "default": false,
    description: 'Run the builder in sandbox mode'
  },

  appCues: {
    type: 'boolean',
    errorFallback: true,
    default: true,
    description: 'Whether we should initialize the App Cues script',
    intendedExpiry: '2018-03-01'
  },

  svgUpload: {
    type: 'boolean',
    errorFallback: false,
    default: false,
    description: 'All SVG image uploading'
  },

  svgColor: {
    type: 'boolean',
    errorFallback: false,
    default: false,
    description: 'Allow users to change colour of SVGs in the builder'
  }

};

//////////////////////////////////////////////////
// support code

var dslFns = (function(){
  var percentOfAccounts = function(threshold) {
    var accountId = window.gon.current_account_id;
    return accountId % 100 <= threshold;
  };
  percentOfAccounts.__argtype__ = 'number';
  percentOfAccounts.__doc__ = 'Unbounce accountId % 100 <= arg';

  var percentOfClients = function(threshold) {
    var clientId = window.gon.client_id;
    return clientId % 100 <= threshold;
  };
  percentOfClients.__argtype__ = 'number';
  percentOfClients.__doc__ = 'Unbounce clientId % 100 <= arg';

  var envIs = function(env) {
    return window.gon && window.gon.env === env;
  };
  envIs.__argtype__ = 'string';
  envIs.__doc__ = 'development, integration, or production';

  var clientId = function(cid) {
    return window.gon && window.gon.client_id === cid;
  };
  clientId.__argtype__ = 'number';
  clientId.__doc__ = 'Unbounce clientId === arg';

  var accountId = function(accountId) {
    return window.gon && window.gon.account_id === accountId;
  };
  accountId.__argtype__ = 'number';
  accountId.__doc__ = 'Unbounce accountId === arg';

  var inChannel = function(channelName) {
    return (window.banzai_lookup.urls &&
            window.banzai_lookup.urls.resolved_via && _.any(
              window.banzai_lookup.urls.resolved_via,
              function(entry) {
                return _.isObject(entry) && entry.channel === channelName;
              }
    ));
  };
  inChannel.__argtype__ = 'string';
  inChannel.__doc__ = 'Bless channel matches arg';

  return {
    "envIs": envIs,
    "clientId": clientId,
    "accountId": accountId,
    "inChannel": inChannel,
    "percentOfAccounts": percentOfAccounts,
    "percentOfClients": percentOfClients};
})();

var queryStringVars = (function() {
  var queryString;
  if (typeof window === 'object') {
    queryString = window.location.search.substr(1).split('&');
  }

  if ( ! queryString){
    return {};
  } else {
    var queryObj = {};

    for (var i = 0; i < queryString.length; i++) {
      var param = queryString[i].split('=', 2);
      if (param.length === 1) {
        queryObj[param[0]] = "";
      } else {
        queryObj[param[0]] = decodeURIComponent(param[1].replace(/\+/g, " "));
      }
    }
    return queryObj;
  }
})();

var _interpretValFromQueryString = function(v, key) {
  try {
    if (banzaiDefinitions[key].type === 'boolean') {
      return Boolean(JSON.parse(v));
    } else if (banzaiDefinitions[key].type === 'number') {
      return parseInt(v, 10);
    } else {
      // treat it like a string
      return v;
    }

  } catch (e) {
    return undefined;
  }
};

var _getOverridesFromQueryString = function() {
  var overrides = _.pick(queryStringVars, _.keys(banzaiDefinitions));
  var interpretedOverrides = _.mapValues(overrides, _interpretValFromQueryString);
  return _.omit(interpretedOverrides, _.isUndefined);
};

var _getOverridesFromBlessSelectors = function(){
  var banzai = typeof window === 'object' ? window.banzai_lookup : undefined;
  if (_.isEmpty(banzai) || _.isEmpty(banzai.urls)) {
    return {};
  }

  try {
    var featuresForSelectors = _.map(banzai.urls.resolved_via, function(step) {
      var entry;
      if (typeof step === 'string') {
        entry = banzai.lookup_table[step];
      } else if (step.channel) {
        entry = banzai.lookup_table.channels[step.channel];
      }
      return entry && entry.features;
    }).reverse();
    return _.reduce(featuresForSelectors, _.merge, {});
  } catch (err) {
    console.trace('Error in _getOverridesFromBlessSelectors', err);
    return {};
  }
};

var _getFloodgateOverrides = function() {
  // returns an {} of {flagName:true} for all boolean flags, if floodgate open
  if (! _interpretValFromQueryString(queryStringVars.floodgate)) {
    return {};
  }
  // else if floodgate open:
  var booleanFlags = _.pick(banzaiDefinitions, function(def) {
    return def && def.type === 'boolean';
  });
  var forcedOn = _.map(booleanFlags, function(def, name) {
    var entry = {};
    entry[name] = true; // because you can't {variable:val}!
    return entry;
  });
  return _.reduce(forcedOn, _.merge, {});
};

var loadBanzai = function() {
  var overrides = _.merge(
    {},
    typeof window === 'object' ? window.banzai_features_table : {}, //From dynamo table
    _getOverridesFromBlessSelectors(),
    _getFloodgateOverrides(),
    _getOverridesFromQueryString()
  );
  return new Banzai(banzaiDefinitions, overrides, dslFns);
};

module.exports = loadBanzai();



// WEBPACK FOOTER //
// ./src/control/banzai-features.js